import { createFileRoute } from '@tanstack/react-router'

import {
  EarningsTeaser,
  ShootJobCard,
  UpcomingShootRow,
  UploadDuePanel,
} from '@/components/fotograf/prehled'
import { JellyIcon } from '@/components/icons'
import { DayRailSection, RatingSheet, SectionLabel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import { CHAT_PREVIEW, SPECIALIST } from '@/mocks/fotograf/chat'
import { VENUE_PICKED, VENUE_REASONS, VENUE_SUBJECT } from '@/mocks/fotograf/foceni'
import { EARNINGS_LINES, NEXT_SHOOT, PHOTOGRAPHER, UPCOMING_DAYS } from '@/mocks/fotograf/prehled'

/** The ask after he hands materials over (spec §9) — a sheet, not a card. */
type PrehledSearch = { info?: 'hodnoceni'; hvezdy?: number }

export const Route = createFileRoute('/fotograf/prehled')({
  validateSearch: (search: Record<string, unknown>): PrehledSearch => ({
    ...(search.info === 'hodnoceni' ? { info: 'hodnoceni' as const } : {}),
    ...(Number(search.hvezdy) >= 1 && Number(search.hvezdy) <= 5
      ? { hvezdy: Number(search.hvezdy) }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Přehled · Aplikace Bitez' }] }),
  component: FotografPrehled,
})

function FotografPrehled() {
  const { info, hvezdy } = Route.useSearch()

  return (
    <div className="px-4 pt-6 pb-6">
      <header className="flex items-center gap-3.5">
        <Link href="/fotograf/profil" aria-label="Váš profil" className="shrink-0">
          <img
            src={PHOTOGRAPHER.avatar}
            alt=""
            width={150}
            height={150}
            className="size-13 rounded-full object-cover shadow-sm ring-2 ring-white transition-transform duration-150 active:scale-95"
          />
        </Link>
        <div className="min-w-0">
          <SectionLabel>Středa 9. dubna</SectionLabel>
          <h1 className="font-display mt-0.5 text-2xl leading-[0.9] uppercase">
            Dobré ráno, {PHOTOGRAPHER.firstName}
          </h1>
        </div>
      </header>

      <div className="animate-rise-up mt-5">
        <UploadDuePanel
          hoursLeft="31 h"
          venue="Bistro Kavka"
          shotAt="včera 14:00"
          href="/fotograf/foceni/bistro-kavka?stav=odevzdani"
        />
      </div>

      {/* His one conversation (B12) — no tab for a single thread, but a face on
          the screen he opens every morning. */}
      <div className="animate-rise-up mt-5" style={{ animationDelay: '60ms' }}>
        <Link href="/fotograf/chat" className="group flex items-center gap-3">
          <img
            src={SPECIALIST.avatar}
            alt=""
            width={120}
            height={120}
            className="size-9 shrink-0 rounded-full object-cover"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-bold">{SPECIALIST.name}</span>
            <span className="block truncate text-[10px] font-semibold text-ink/45">
              {CHAT_PREVIEW.text}
            </span>
          </span>
          <span className="shrink-0 text-[10px] font-semibold text-ink/40">
            {CHAT_PREVIEW.time}
          </span>
          <JellyIcon
            name="arrow-right"
            className="size-3.5 shrink-0 text-ink/40 transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </Link>
      </div>

      <div className="animate-rise-up mt-6" style={{ animationDelay: '90ms' }}>
        <ShootJobCard job={NEXT_SHOOT} />
      </div>

      <section className="animate-rise-up mt-8" style={{ animationDelay: '180ms' }}>
        <SectionLabel>Dál v kalendáři</SectionLabel>
        <div className="mt-4">
          {UPCOMING_DAYS.map((group, groupIndex) => (
            <DayRailSection
              key={group.date}
              day={group.day}
              date={group.date}
              last={groupIndex === UPCOMING_DAYS.length - 1}
            >
              <div className="space-y-3 pb-6">
                {group.shoots.map((shoot) => (
                  <UpcomingShootRow key={shoot.venue} shoot={shoot} />
                ))}
              </div>
            </DayRailSection>
          ))}
        </div>
      </section>

      {/* Money without opening the Výdělky tab — and pointed forward: what is
          still coming and when it lands, not how last month did. */}
      <section className="animate-rise-up mt-2" style={{ animationDelay: '270ms' }}>
        <SectionLabel>Vaše peníze</SectionLabel>
        <div className="mt-3">
          <EarningsTeaser month="Duben zatím" amount="1 550 Kč" lines={EARNINGS_LINES} />
        </div>
      </section>

      {/* His trigger is the handover, not the shoot: a photographer standing in
          a kitchen rates nobody, and by the time he uploads he knows exactly how
          it went. The stars land him on that shoot, where the reasons are. */}
      {info === 'hodnoceni' && (
        <RatingSheet
          closeHref="/fotograf/prehled"
          question="Jak se u nich fotilo?"
          subject={VENUE_SUBJECT}
          stars={hvezdy}
          reasons={VENUE_REASONS}
          picked={hvezdy ? VENUE_PICKED : []}
          notePlaceholder="Co pomohlo, co příště nachystat…"
          privacy="Vidí to Bára. Klientovi se nezobrazuje."
          hrefFor={(stars) => `/fotograf/prehled?info=hodnoceni&hvezdy=${stars}`}
        />
      )}
    </div>
  )
}
